import { useNavigation } from "@react-navigation/native";
import DataIcon from "assets/images/defaultIcons/data.svg";
import theme from "assets/theme";
import { formatDatetime } from "helpers/shared.helpers";
import moment from "moment";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  Image,
  Linking,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import AlertService from "services/Alert.service";
import { ALERT_TYPE_INFO } from "shared/constants";
import { ALERT_CONFIRMATION_MESSAGES } from "shared/constants/alert.constants";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import CustomTextEditor from "webComponentsFormElements/inputs/CustomTextEditor.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import AlertRecipientSummary from "webComponentsScreens/alerts/AlertRecipientSummary.component";
import CreateAlertModal from "webComponentsScreens/alerts/CreateAlertModal.component";
import { AlertContext } from "webContext/Alert.context";
import { AuthContext } from "webContext/Auth.context";
import { ConfirmationContext } from "webContext/Confirmation.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";
import PropTypes from "prop-types";

const classes = StyleSheet.create({
  leftContainer: {
    flex: 1,
    gap: 10,
  },
  rightContainer: {
    gap: 10,
  },
  titleContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "wrap",
    overflow: "hidden",
    gap: 2,
  },
  dismissedText: {
    color: theme.color.darkGrey,
    fontWeight: theme.font.weight.medium,
    fontSize: 14,
    fontStyle: "italic",
  },
  alertPrimaryInformation: {
    paddingLeft: 10,
    gap: 15,
  },
  header: {
    fontSize: 16,
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    marginBottom: 8,
  },
  systemCardContainer: {
    paddingTop: 4,
    paddingLeft: 4,
    paddingBottom: 8,
    gap: 10,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  systemCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: theme.color.lightGrey,
    borderRadius: 6,
    padding: 10,
    gap: 6,
  },
  image: {
    width: 50,
    height: 50,
    borderRadius: 4,
  },
  systemInfo: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  systemTitle: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    fontSize: theme.font.size.base,
    flexWrap: "wrap",
  },
  systemStatus: {
    fontWeight: theme.font.weight.medium,
  },
  label: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
  },
  value: {
    color: theme.color.darkGrey,
  },
  noneText: {
    paddingLeft: 10,
    color: theme.color.darkGrey,
    fontStyle: "italic",
  },
  linkItem: {
    paddingLeft: 10,
    paddingVertical: 2,
    color: theme.color.primary,
    textDecorationLine: "underline",
  },
  buttonContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 10,
  },
  expiredBadge: {
    backgroundColor: theme.color.red,
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 8,
    alignSelf: "flex-end",
  },
  expiredBadgeText: {
    color: theme.color.white,
    fontSize: 11,
    fontWeight: theme.font.weight.bold,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
});

export default function AlertCard(props) {
  const {
    alert,
    fromTable = false,
    openAlert,
    onDelete,
    showDeleted = false,
  } = props;

  const { organizationSystemMap, organizationColor } =
    useContext(OrganizationContext);
  const { getAlerts } = useContext(AlertContext);
  const { showToast } = useContext(ToastContext);
  const { showConfirmation } = useContext(ConfirmationContext);
  const { isMessagingManager } = useContext(AuthContext);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isEditingAlert, setIsEditingAlert] = useState(
    openAlert ? { ...alert } : null,
  );
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [resolutionText, setResolutionText] = useState("");
  const navigation = useNavigation();

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        root: {
          backgroundColor: theme.color.background,
          borderWidth: !fromTable ? 2 : 0,
          borderColor: alert.isActive
            ? organizationColor
            : theme.color.darkGrey,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: 15,
          margin: 10,
          padding: 15,
          flexDirection: "row",
          maxWidth: 600,
        },
        title: {
          fontSize: theme.font.size.subHeader,
          fontWeight: theme.font.weight.semibold,
          color: alert.isActive ? organizationColor : theme.color.darkGrey,
          flex: 1,
          flexWrap: "wrap",
          minWidth: 0,
        },
        icon: {
          ...classes.image,
          fill: organizationColor,
        },
      }),
    [organizationColor, alert.isActive, fromTable],
  );

  const creator = alert._associations.createUser;

  const isInfoMessage = alert.type === ALERT_TYPE_INFO;
  const isExpired =
    isInfoMessage &&
    !!alert.isActive &&
    !!alert.expirationDatetime &&
    new Date(alert.expirationDatetime) < new Date();

  const handleDelete = useCallback(async () => {
    showConfirmation({
      prompt: isInfoMessage
        ? ALERT_CONFIRMATION_MESSAGES.deleteInfoMessage.message
        : ALERT_CONFIRMATION_MESSAGES.deleteAlert.message,
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await AlertService.deleteAlert(alert.alertId);
          await getAlerts(showDeleted);
          onDelete?.();
          showToast(
            `${isInfoMessage ? "Info Message" : "Alert"} "${alert.title}" deleted successfully.`,
            "success",
          );
        } catch (error) {
          if (__DEV__) {
            console.error("Failed to delete alert:", error);
          }
          showToast("Failed to delete alert.", "error");
        } finally {
          setIsDeleting(false);
        }
      },
    });
  }, [
    showConfirmation,
    alert,
    getAlerts,
    showToast,
    isInfoMessage,
    onDelete,
    showDeleted,
  ]);

  const handleResolveAlert = useCallback(async () => {
    setShowResolutionModal(true);
  }, []);

  const handleConfirmResolve = useCallback(async () => {
    setIsResolving(true);
    try {
      await AlertService.upsertAlert({
        alert: {
          alertId: alert.alertId,
          isActive: false,
          title: alert.title,
          description: alert.description || null,
          startDateTime: formatDatetime(alert.startDatetime),
          resolution: resolutionText.trim() || "No resolution provided",
        },
      });

      await getAlerts(showDeleted);
      setShowResolutionModal(false);
      setResolutionText("");

      showToast(`Alert "${alert.title}" resolved successfully.`, "success");
    } catch (error) {
      if (__DEV__) {
        console.error("Failed to resolve alert:", error);
      }
      showToast("Failed to resolve alert.", "error");
    } finally {
      setIsResolving(false);
    }
  }, [alert, getAlerts, showToast, resolutionText, showDeleted]);

  const modals = (
    <>
      <CustomModal
        open={showResolutionModal}
        onClose={() => {
          setShowResolutionModal(false);
          setResolutionText("");
        }}
        title="Resolve Alert"
        onSave={handleConfirmResolve}
        canSave={true}
        isSaving={isResolving}
        saveTitle="Resolve"
      >
        <View style={{ padding: 20, minWidth: 400 }}>
          <Text style={classes.header}>Resolution Details</Text>
          <LabelInput
            label="Please provide a resolution for this alert:"
            value={resolutionText}
            onChangeText={setResolutionText}
            placeholder="Describe how this alert was resolved..."
            multiline
            numberOfLines={6}
            style={{ height: 150 }}
          />
        </View>
      </CustomModal>
      <CreateAlertModal
        visible={String(isEditingAlert?.alertId) === String(alert.alertId)}
        existingAlert={isEditingAlert?.alertId === alert.alertId && alert}
        showDeleted={showDeleted}
        onClose={() => {
          setIsEditingAlert(null);
          navigation.setParams({ alertId: undefined });
        }}
      />
    </>
  );

  if (isInfoMessage) {
    return (
      <View style={stateClasses.root}>
        <View style={[classes.leftContainer, { minWidth: 0 }]}>
          <View style={classes.titleContainer}>
            <Text style={stateClasses.title}>{alert.title}</Text>
            {isExpired && (
              <View style={classes.expiredBadge}>
                <Text style={classes.expiredBadgeText}>Expired</Text>
              </View>
            )}
          </View>

          <View style={[classes.alertPrimaryInformation, { minWidth: 0 }]}>
            <View style={{ minWidth: 0 }}>
              <Text style={classes.header}>Details</Text>
              {alert.description ? (
                <CustomTextEditor readOnly initialText={alert.description} />
              ) : (
                <Text style={classes.noneText}>No additional details.</Text>
              )}
            </View>

            {alert._associations?.links?.length > 0 && (
              <View style={{ paddingRight: 10 }}>
                <Text style={classes.header}>Links</Text>
                {alert._associations.links.map((link) => (
                  <Pressable
                    key={link.alertLinkId}
                    onPress={() => Linking.openURL(link.url)}
                  >
                    <Text style={classes.linkItem}>
                      {link.label || link.url}
                    </Text>
                  </Pressable>
                ))}
              </View>
            )}
          </View>
        </View>

        <View style={classes.rightContainer}>
          {!alert.isActive && (
            <View>
              <Text style={classes.label}>Resolution:</Text>
              <Text style={classes.value}>
                {alert.resolution || "No resolution provided"}
              </Text>
            </View>
          )}
          <View>
            <Text style={classes.label}>Last Edited:</Text>
            <Text style={classes.value}>
              {alert.changeDatetime
                ? moment(alert.changeDatetime).format("MMM DD, YYYY hh:mm A")
                : "--"}
            </Text>
          </View>
          <View>
            <Text style={classes.label}>Creator:</Text>
            <Text style={classes.value}>
              {creator ? `${creator.firstName} ${creator.lastName}` : "Unknown"}
            </Text>
          </View>
          <View style={classes.buttonContainer}>
            <CustomButton
              title="Delete"
              variant="danger"
              icon="delete"
              onPress={handleDelete}
              disabled={alert.isDeleted || isDeleting}
              isLoading={isDeleting}
            />
            {!fromTable && (
              <>
                <CustomButton
                  title="Edit"
                  variant="dark"
                  icon="pencil"
                  disabled={!alert.isActive || alert.isDeleted}
                  onPress={() => setIsEditingAlert({ ...alert })}
                />
                <CustomButton
                  title="Resolve"
                  variant="dark"
                  icon="check-circle"
                  disabled={!alert.isActive || isResolving}
                  isLoading={isResolving}
                  onPress={handleResolveAlert}
                />
              </>
            )}
          </View>
          {modals}
        </View>
      </View>
    );
  }

  return (
    <View style={stateClasses.root}>
      <View style={classes.leftContainer}>
        <View style={classes.titleContainer}>
          <Text style={stateClasses.title}>{alert.title}</Text>
          {!alert.isActive && (
            <Text style={classes.dismissedText}> (dismissed)</Text>
          )}
        </View>

        <View style={classes.alertPrimaryInformation}>
          <View>
            <Text style={classes.header}>Details</Text>
            {alert.description ? (
              <CustomTextEditor readOnly initialText={alert.description} />
            ) : (
              <Text style={classes.noneText}>No additional details.</Text>
            )}
          </View>
          <View>
            <Text style={classes.header}>Affected Platforms</Text>
            {alert._associations?.systemAlertMap.length > 0 ? (
              <View style={classes.systemCardContainer}>
                {alert._associations?.systemAlertMap.map((systemAlert) => {
                  const platform =
                    systemAlert._associations?.organizationSystem;
                  const fullPlatform = organizationSystemMap.get(
                    platform.organizationSystemId,
                  );

                  return (
                    <View
                      key={fullPlatform.organizationSystemId}
                      style={classes.systemCard}
                    >
                      {fullPlatform.signedFileUrl ? (
                        <Image
                          source={{ uri: fullPlatform.signedFileUrl }}
                          resizeMode="contain"
                          style={classes.image}
                        />
                      ) : (
                        <DataIcon style={stateClasses.icon} />
                      )}
                      <View style={classes.systemInfo}>
                        <Text style={classes.systemTitle}>
                          {fullPlatform.title}
                        </Text>
                        <Text
                          style={[
                            classes.systemStatus,
                            {
                              color: platform.isOnline
                                ? theme.color.green
                                : theme.color.red,
                            },
                          ]}
                        >
                          {platform.isOnline ? "Online" : "Offline"}
                        </Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            ) : (
              <Text style={classes.noneText}>No platforms affected</Text>
            )}
          </View>
          <View>
            <Text style={classes.header}>Recipients</Text>
            <AlertRecipientSummary
              teamIds={alert._associations.teamAlertMap.map(
                (teamAlert) => teamAlert.teamId,
              )}
              locationIds={alert._associations.locationAlertMap.map(
                (locationAlert) => locationAlert.locationId,
              )}
              userIds={alert._associations.recipientObjects.map(
                (person) => person.personId,
              )}
            />
          </View>
        </View>
      </View>
      <View style={classes.rightContainer}>
        <View>
          <Text style={classes.label}>Start time:</Text>
          <Text style={classes.value}>
            {moment(alert.startDatetime).format("MMM DD, YYYY hh:mm A")}
          </Text>
        </View>
        {!alert.isActive && (
          <View>
            <Text style={classes.label}>Resolution:</Text>
            <Text style={classes.value}>
              {alert.resolution || "No resolution provided"}
            </Text>
          </View>
        )}
        <View>
          <Text style={classes.label}>Last Edited:</Text>
          <Text style={classes.value}>
            {alert.changeDatetime
              ? moment(alert.changeDatetime).format("MMM DD, YYYY hh:mm A")
              : "--"}
          </Text>
        </View>
        <View>
          <Text style={classes.label}>Creator:</Text>
          <Text style={classes.value}>
            {creator ? `${creator.firstName} ${creator.lastName}` : "Unknown"}
          </Text>
        </View>
        <View style={classes.buttonContainer}>
          <CustomButton
            title="Delete"
            variant="danger"
            icon="delete"
            onPress={handleDelete}
            disabled={alert.isDeleted || (isMessagingManager && !isInfoMessage) || isDeleting}
            isLoading={isDeleting}
          />
          {!fromTable && (
            <View style={classes.buttonContainer}>
              <CustomButton
                title="Edit"
                variant="dark"
                icon="pencil"
                disabled={!alert.isActive || alert.isDeleted}
                onPress={() => {
                  setIsEditingAlert({
                    ...alert,
                  });
                }}
              />
              <CustomButton
                title="Resolve"
                variant="dark"
                icon="check-circle"
                disabled={!alert.isActive || isResolving}
                isLoading={isResolving}
                onPress={handleResolveAlert}
              />
            </View>
          )}
        </View>
        {modals}
      </View>
    </View>
  );
}

AlertCard.propTypes = {
  alert: PropTypes.object.isRequired,
  fromTable: PropTypes.bool,
  openAlert: PropTypes.bool,
  onDelete: PropTypes.func,
  showDeleted: PropTypes.bool,
};
